import Layout, { Content } from 'antd/lib/layout/layout';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { getAutomationIdByType, innerStepStatusColorForReleaseStepStatus } from '../helpers';
import { RootState } from '../reducers/reducers';
import { ReleaseStepVCSDistribution, ReleaseStepStatus, AutomationType } from '../types';
import AutomatedTakeoffTasks from './AutomatedTakeoffTasks';
import Text from 'antd/lib/typography/Text';
import styles from './styles/stepStyles';
import Space from 'antd/lib/space';
import WorkflowBuildModule from './workflowModule/WorkflowBuildModule';
import Link from 'antd/lib/typography/Link';
import { selectRelease, selectAppSettings, selectReleaseStep } from '../selectors/selectors';
import { ReleaseEventsTimelineSection } from './releaseOverview/ReleaseTimelineSection';
import { ReleaseTimelineSectionContext } from './timelineEvents/EventsTimeline';
import { ReleaseStepOrStepRunComponentProps } from '@/components/ValidatedRouteComponents';

const StepContentVCSDistribution: React.FC<ReleaseStepOrStepRunComponentProps> = (props) => {
    const { appId, releaseId, releaseStepType } = props;

    const releaseStep = useSelector((state: RootState) =>
        selectReleaseStep(state, releaseId, releaseStepType),
    ) as ReleaseStepVCSDistribution;
    const releaseStepData = releaseStep.data;
    const prodBuild = releaseStepData.prodBuild;
    const stepStatus = releaseStep.status;
    const userId = useSelector((state: RootState) => state.user.id);

    const autoUploadDsymsToStabilityMonitoringAutomationId = getAutomationIdByType(
        releaseStep.automationRuns,
        AutomationType.AutoUploadDsymsToStabilityMonitoring,
    );

    const autoUploadBinaryArtifactToAppStoreAutomationId = getAutomationIdByType(
        releaseStep.automationRuns,
        AutomationType.AutoUploadBinaryArtifactToAppStore,
    );

    const autoDownloadArtifactsAutomationId = getAutomationIdByType(
        releaseStep.automationRuns,
        AutomationType.AutoDownloadArtifacts,
    );

    const appPlatform = useSelector((state: RootState) => selectAppSettings(state, appId).platform);
    const releaseVersion = useSelector((state: RootState) => selectRelease(state, props.releaseId).version);

    const isReleased = !!releaseStep.data.publishedOn;

    const titleForState = (stepStatus: ReleaseStepStatus, isReleased: boolean) => {
        switch (stepStatus) {
            case ReleaseStepStatus.Inactive:
                return 'Not ready to release';
            case ReleaseStepStatus.Pending:
                return isReleased ? 'SDK has been updated' : 'Not released';
            case ReleaseStepStatus.Ready:
                return 'SDK has been updated';
            default:
                return null;
        }
    };

    const descriptionForState = (repoName: string, stepStatus: ReleaseStepStatus, releaseUrl?: string) => {
        switch (stepStatus) {
            case ReleaseStepStatus.Blocked:
                return null;
            case ReleaseStepStatus.Inactive:
                return (
                    <Text type="secondary">
                        You&apos;ll release your update through your public{' '}
                        {
                            <Text type="secondary" strong>
                                {repoName ?? ''}
                            </Text>
                        }{' '}
                        repo.
                    </Text>
                );
            case ReleaseStepStatus.Pending:
                return (
                    <Text type="secondary">
                        Update your{' '}
                        {
                            <Text type="secondary" strong>
                                {repoName ?? ''}
                            </Text>
                        }{' '}
                        package via GitHub.
                    </Text>
                );
            case ReleaseStepStatus.Ready:
            case ReleaseStepStatus.PendingChecklistOnly:
                return (
                    <Text type="secondary">
                        Your updated SDK is now{' '}
                        {
                            <Link href={releaseUrl} target="_blank">
                                available on GitHub.
                            </Link>
                        }
                    </Text>
                );
        }
    };

    return (
        <Layout>
            <Content
                style={{
                    ...styles.innerContent,
                    paddingTop: 0,
                }}
            >
                <div style={{ display: 'flex', marginBottom: 30, justifyContent: 'space-between' }}>
                    <Space direction="vertical" size={4} style={{ paddingTop: 28 }}>
                        <Text
                            style={{
                                fontSize: 30,
                                color: innerStepStatusColorForReleaseStepStatus(stepStatus),
                                fontWeight: 600,
                            }}
                        >
                            {titleForState(stepStatus, isReleased)}
                        </Text>
                        {descriptionForState(
                            releaseStepData.externalPublicRepoName,
                            stepStatus,
                            releaseStepData.externalReleaseUrl,
                        )}
                    </Space>
                    <ReleaseEventsTimelineSection
                        orgId={props.orgId}
                        appId={props.appId}
                        releaseId={props.releaseId}
                        stepRunId={props.stepRunComponentProps?.stepRunId}
                        context={ReleaseTimelineSectionContext.ReleaseStep}
                        releaseStepType={props.releaseStepType}
                        shouldShowFullEventTimeline={props.shouldShowFullEventTimeline}
                    />
                </div>
                {releaseStepData.workflowData && (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'stretch',
                            flexShrink: 0,
                            width: '100%',
                            marginBottom: 24,
                        }}
                    >
                        <WorkflowBuildModule
                            orgId={props.orgId}
                            appId={props.appId}
                            releaseId={props.releaseId}
                            releaseStepType={props.releaseStepType}
                            releaseStepStatus={stepStatus}
                            platform={appPlatform}
                            workflowData={releaseStepData.workflowData}
                            buildInfo={prodBuild}
                            autoUploadDsymsToStabilityMonitoringAutomationId={
                                autoUploadDsymsToStabilityMonitoringAutomationId
                            }
                            autoUploadBinaryArtifactToAppStoreAutomationId={
                                autoUploadBinaryArtifactToAppStoreAutomationId
                            }
                            autoDownloadArtifactsAutomationId={autoDownloadArtifactsAutomationId}
                            stepRunComponentProps={props.stepRunComponentProps}
                        />
                    </div>
                )}
                <AutomatedTakeoffTasks
                    stepRunComponentProps={props.stepRunComponentProps}
                    orgId={props.orgId}
                    predictedTagName={releaseStepData.tagData.expectedReleaseTagName}
                    releaseTagState={releaseStepData.tagData.releaseTagState}
                    releaseTag={releaseStepData.tagData.releaseTag}
                    releaseVersion={releaseVersion}
                    appId={appId}
                    releaseId={releaseId}
                    userId={userId}
                    codeDiffUrl={releaseStepData.tagData.codeDiffUrl}
                />
            </Content>
        </Layout>
    );
};

export default StepContentVCSDistribution;
